import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import ReplaysList from './components/ReplaysList';
import ParsedReplayView from './components/ParsedReplayView';
import Sc2Calculator from './components/sc2/Sc2Calculator';
import { createAuthenticatedAxios, getStoredToken, isTokenExpired, generateJwtToken, storeToken, logEnvironmentInfo, forceTokenRegeneration } from './authUtils';

// Check if we're in production environment
const isProduction = () => {
  // Multiple checks to ensure we detect production correctly
  return process.env.NODE_ENV === 'production' || 
         window.location.hostname !== 'localhost' ||
         window.location.protocol === 'https:';
};

// Determine API base URL based on environment
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (isProduction()) {
    return '/api';
  }
  return 'http://localhost:8082/api';
};

const API_BASE_URL = getApiBaseUrl();

function App() {
  const [replays, setReplays] = useState([]);
  const [selectedReplay, setSelectedReplay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const [view, setView] = useState(() => (
    new URLSearchParams(window.location.search).get('view') === 'calculator' ? 'calculator' : 'replays'
  ));

  // Log environment info on component mount (for debugging)
  useEffect(() => {
    logEnvironmentInfo();
  }, []);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = (event) => {
      console.log('[Navigation] Browser back/forward detected');
      if (event.state && event.state.selectedReplayId) {
        // Find the replay by ID and select it
        const replay = replays.find(r => r.replay_id === event.state.selectedReplayId);
        if (replay) {
          console.log('[Navigation] Restoring selected replay:', replay.replay_id);
          setSelectedReplay(replay);
        }
      } else {
        console.log('[Navigation] Returning to replay list');
        setSelectedReplay(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [replays]);

  const ensureValidToken = useCallback(async (forceRegenerate = false) => {
    const storedToken = getStoredToken();
    if (!forceRegenerate && storedToken && !isTokenExpired(storedToken)) {
      return storedToken;
    }

    // Generate new token if none exists, expired, or forced regeneration
    if (!isGeneratingToken) {
      setIsGeneratingToken(true);
      try {
        const result = forceRegenerate ? 
          await forceTokenRegeneration() : 
          await generateJwtToken();
          
        if (result.success) {
          storeToken(result.token);
          setAuthToken(result.token);
          return result.token;
        } else {
          throw new Error(result.error?.message || 'Failed to generate token');
        }
      } catch (err) {
        setError(`Authentication failed: ${err.message}`);
        return null;
      } finally {
        setIsGeneratingToken(false);
      }
    }
    return null;
  }, [isGeneratingToken]);

  const fetchReplays = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Ensure we have a valid token
      const token = await ensureValidToken();
      if (!token) {
        setError('Failed to obtain authentication token');
        return;
      }
      
      const axiosInstance = createAuthenticatedAxios(token);
      const response = await axiosInstance.get('/replays/list');
      setReplays(response.data.replays || []);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 429) {
        // Try to regenerate token on 401 (Unauthorized) or 429 (Too Many Requests)
        console.log(`Received ${err.response.status} error, regenerating token...`);
        
        // Force regeneration for 429 errors to ensure we get a fresh token
        const forceRegenerate = err.response?.status === 429;
        const newToken = await ensureValidToken(forceRegenerate);
        
        if (newToken) {
          // Retry the request with new token
          try {
            const axiosInstance = createAuthenticatedAxios(newToken);
            const response = await axiosInstance.get('/replays/list');
            setReplays(response.data.replays || []);
            return;
          } catch (retryErr) {
            if (retryErr.response?.status === 429) {
              setError('Rate limit exceeded. Please try again in a few minutes.');
            } else {
              setError('Authentication failed after token regeneration');
            }
          }
        } else {
          setError('Authentication required but token generation failed');
        }
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to fetch replays');
      }
    } finally {
      setLoading(false);
    }
  }, [ensureValidToken]);

  useEffect(() => {
    // Check for stored token on app start and initialize
    const storedToken = getStoredToken();
    if (storedToken && !isTokenExpired(storedToken)) {
      setAuthToken(storedToken);
    }
    // Start fetching replays (will auto-generate token if needed)
    fetchReplays();
  }, [fetchReplays]);

  // Initialize state from URL on page load
  useEffect(() => {
    if (replays.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const replayId = urlParams.get('replay');
      if (replayId) {
        const replay = replays.find(r => r.replay_id === parseInt(replayId));
        if (replay) {
          console.log('[Navigation] Initializing from URL, selecting replay:', replayId);
          setSelectedReplay(replay);
        }
      }
    }
  }, [replays]);

  const handleReplaySelect = (replay) => {
    console.log('[Navigation] Selecting replay:', replay.replay_id);
    setSelectedReplay(replay);
    
    // Push new state to browser history
    const newState = { selectedReplayId: replay.replay_id };
    const newUrl = `${window.location.pathname}?replay=${replay.replay_id}`;
    window.history.pushState(newState, '', newUrl);
  };

  const handleBackToList = () => {
    console.log('[Navigation] Returning to list via back button');
    setSelectedReplay(null);
    
    // Push state for list view
    window.history.pushState(null, '', window.location.pathname);
  };

  const handleViewChange = (nextView) => {
    setView(nextView);
    const url = nextView === 'calculator'
      ? `${window.location.pathname}?view=calculator`
      : window.location.pathname;
    window.history.pushState(null, '', url);
  };

  if (view === 'replays' && (loading || isGeneratingToken)) {
    return (
      <div className="App">
        <div className="loading">
          {isGeneratingToken ? 'Generating authentication token...' : 'Loading replays...'}
        </div>
      </div>
    );
  }

  if (view === 'replays' && error) {
    return (
      <div className="App">
        <div className="error">
          Error: {error}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={fetchReplays} className="retry-btn">
              Retry
            </button>
            <button onClick={() => handleViewChange('calculator')} className="retry-btn">
              Открыть калькулятор
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>StarCraft II Replays Viewer</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <nav className="view-nav">
            <button
              onClick={() => handleViewChange('replays')}
              className={`view-nav-btn ${view === 'replays' ? 'active' : ''}`}
            >
              Реплеи
            </button>
            <button
              onClick={() => handleViewChange('calculator')}
              className={`view-nav-btn ${view === 'calculator' ? 'active' : ''}`}
            >
              🧮 Калькулятор
            </button>
          </nav>
          {view === 'replays' && selectedReplay && (
            <button onClick={handleBackToList} className="back-btn">
              ← Back to List
            </button>
          )}
        </div>
      </header>
      
      <main className="App-main">
        {view === 'calculator' ? (
          <Sc2Calculator />
        ) : selectedReplay ? (
          <ParsedReplayView 
            replay={selectedReplay}
            apiBaseUrl={API_BASE_URL}
          />
        ) : (
          <ReplaysList 
            replays={replays} 
            onReplaySelect={handleReplaySelect}
            onRefresh={fetchReplays}
          />
        )}
      </main>
    </div>
  );
}

export default App;
